/**
 * Formats a number using Intl.NumberFormat with configurable locale and fraction digits
 * @param {number} num - The number to format
 * @param {Object} [options] - Configuration options for number formatting
 * @param {number} [options.minimumFractionDigits=2] - Minimum number of fraction digits to show
 * @param {number} [options.maximumFractionDigits=2] - Maximum number of fraction digits to show
 * @param {string} [options.locale="en-IN"] - The locale to use for number formatting
 * @returns {string} The formatted number string
 */
export const formatNumber = (
  num: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    locale?: string;
  }
) => {
  if (isNaN(num)) {
    num = 0;
  }
  const hasDecimal = num % 1 !== 0;
  const {
    minimumFractionDigits = hasDecimal ? 1 : 0,
    maximumFractionDigits = hasDecimal ? 2 : 0,
    locale = "en-IN",
  } = options || {};
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
};

/**
 * Formats a number as currency with proper locale-specific formatting
 * @param {number} amount - The amount to format as currency
 * @param {string} [locale="en-IN"] - The locale to use for currency formatting
 * @param {string} [currency="INR"] - The currency code to use
 * @returns {string} The formatted currency string with a space between symbol and number
 */
export const currencify = (
  amount: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
  locale: string = "en-IN",
  currency: string = "INR"
) => {
  if (isNaN(amount)) {
    amount = 0;
  }

  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = options || {};

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

/**
 * Formats a date string to a short localized date format
 * @param {string} dateStr - The date string to format
 * @returns {string} The formatted date string in "MMM DD, YYYY" format, or "Invalid Date" if input is invalid
 */
export const formatToShortDate = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Invalid Date";
  const options = { year: "numeric", month: "short", day: "numeric" } as const;
  return date.toLocaleDateString("en-US", options);
};

/**
 * Formats a date string or number into a localized date string
 * @param {string|number} date - The date to format (string or YYYYMM format number)
 * @param {Object} [options] - Configuration options for date formatting
 * @param {("numeric"|"2-digit")} [options.day="2-digit"] - The day format
 * @param {("numeric"|"2-digit"|"long"|"short"|"narrow")} [options.month="short"] - The month format
 * @param {("numeric"|"2-digit")} [options.year="2-digit"] - The year format
 * @returns {string} The formatted date string
 */
export const formatDate = (
  date: string | number,
  options?: {
    day?: "numeric" | "2-digit";
    month?: "numeric" | "2-digit" | "long" | "short" | "narrow";
    year?: "numeric" | "2-digit";
    format?: "default" | "dmy"; // default: locale default, dmy: 25 Aug 2025
  }
) => {
  let d: Date;

  if (typeof date === "number") {
    const year = Math.floor(date / 100);
    const month = date % 100;
    d = new Date(year, month - 1);
  } else {
    d = new Date(date);
  }

  const { day = "2-digit", month = "short", year = "2-digit", format = "default" } = options || {};

  if (format === "dmy") {
    const dayStr = d.getDate().toString().padStart(2, "0");
    const monthStr = d.toLocaleString("en-US", { month });
    const yearStr = d.getFullYear();
    return `${dayStr} ${monthStr} ${yearStr}`;
  }

  return d.toLocaleString("en-US", { day, month, year });
};

/**
 * Formats a date string or number into a localized month and year string
 * @param {string|number} date - The date to format (string or YYYYMM format number)
 * @param {Object} [options] - Configuration options for month and year formatting
 * @param {("numeric"|"2-digit"|"long"|"short"|"narrow")} [options.month="short"] - The month format
 * @param {("numeric"|"2-digit")} [options.year="2-digit"] - The year format
 * @returns {string} The formatted month and year string
 */
export const formatMonthYear = (
  date: string | number,
  options?: {
    month?: "numeric" | "2-digit" | "long" | "short" | "narrow";
    year?: "numeric" | "2-digit";
  }
) => {
  let d = new Date(date);
  if (typeof date === "number") {
    const year = Math.floor(date / 100);
    const month = date % 100;
    d = new Date(year, month - 1);
  }

  const { month = "short", year = "2-digit" } = options || {};
  return d.toLocaleString("en-US", {
    month,
    year,
  });
};

export const formatDateTime = (input: string, formatType: "slash" | "short" = "short") => {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "Invalid date";

  const day = date.getDate().toString().padStart(2, "0");
  const monthShort = date.toLocaleString("en-US", { month: "short" });
  const monthNum = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  const timeString = `${hours}:${minutes} ${ampm}`;

  if (formatType === "slash") {
    return `${day}/${monthNum}/${year} at ${timeString}`;
  } else {
    return `${day} ${monthShort} ${year} at ${timeString}`;
  }
};

export const formatMonthYearShort = (date: number): string => {
  const year = date.toString().slice(0, 4);
  const month = parseInt(date.toString().slice(4), 10);

  const shortMonth = new Date(0, month - 1).toLocaleString("en-US", {
    month: "short",
  });
  const shortYear = year.slice(2);

  return `${shortMonth} ${shortYear}`;
};

/**
 * Converts "HH:mm:ss" (24-hour) to "HH:mm AM/PM" format.
 * Example: "16:25:00" → "16:25 PM"
 */
export function convertTo24HourWithAMPM(time24?: string): string {
  if (!time24?.includes(":")) return "";

  const [hourStr = "", minuteStr = ""] = time24.split(":");
  const hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  return `${hourStr}:${minuteStr} ${ampm}`;
}

export const formatNumValue = (num: number) => num.toLocaleString("en-IN");

export const getReturnColorClass = (value: string | number) => {
  const numericValue = Number(value);

  if (numericValue < 0) return "text-red-500";
  return "text-green-500";
};
